import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { Language } from '@/config/languages';

// Dynamic import function for translation files
const loadTranslation = async (language: Language) => {
  switch (language) {
    case Language.EN:
      return (await import('@/locales/en.json')).default;
    case Language.ZH:
      return (await import('@/locales/zh.json')).default;
    default:
      return (await import('@/locales/en.json')).default;
  }
};

// Initialize i18n and load initial translations
const initializeI18n = async () => {
  // First, initialize i18n with basic config
  await i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      fallbackLng: Language.EN,
      debug: process.env.NODE_ENV === 'development',
      // Only load current language, don't try to load other languages
      load: 'currentOnly',
      // Start with empty resources - we'll load them manually
      resources: {},
      interpolation: {
        escapeValue: false, // React already does escaping
      },
      detection: {
        order: ['localStorage'],
        caches: ['localStorage'],
      },
    });

  // Then load the initial language translations
  const detectedLanguage = i18n.language as Language;
  await loadLanguage(detectedLanguage);
};

// Start initialization
initializeI18n();

// Function to load translation for a specific language
export const loadLanguage = async (language: Language) => {
  try {
    const translations = await loadTranslation(language);
    i18n.addResourceBundle(language, 'translation', translations, true, true);
    return true;
  } catch (error) {
    console.error(`Failed to load translations for ${language}:`, error);
    return false;
  }
};

// Function to change language with dynamic loading
export const changeLanguage = async (language: Language) => {
  try {
    // Always load the language first (it will be cached if already loaded)
    const loaded = await loadLanguage(language);

    if (loaded) {
      return await i18n.changeLanguage(language);
    } else {
      throw new Error(`Failed to load translations for ${language}`);
    }
  } catch (error) {
    console.warn(`Failed to load ${language}, falling back to English:`, error);
    // Fallback to English if loading fails
    await loadLanguage(Language.EN);
    return await i18n.changeLanguage(Language.EN);
  }
};

export default i18n;
